import { print } from 'graphql/index';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import { updateOrderExerciseLessonMutation } from '~/shared/graphql-queries';
import { lessonExerciseFragment } from '~/shared/graphql-queries/lesson-exercises';
import createOneLessonExerciseMutation from '~/shared/graphql-queries/lesson-exercises/create-one-lesson-exercise-mutation';
import findByIdLessonExerciseQuery from '~/shared/graphql-queries/lesson-exercises/find-by-id-lesson-exercise-query';
import removeByIdLessonExerciseMutation from '~/shared/graphql-queries/lesson-exercises/remove-by-id-lesson-exercise-mutation';
import removeManyLessonExerciseMutation from '~/shared/graphql-queries/lesson-exercises/remove-many-lesson-exercise-mutation';
import updateByIdLessonExerciseMutation from '~/shared/graphql-queries/lesson-exercises/update-by-id-lesson-exercise-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationLessonExerciseResponse = Readonly<{
  data?: {
    paginationLessonExerciseQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationLessonExerciseResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type CreateOneLessonExerciseArgs = Readonly<{
  record?: Record<string, unknown>;
}>;

type UpdateOrderExerciseLessonArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type RemoveByIdLessonExerciseArgs = Readonly<{
  _id?: string;
}>;

type UpdateByIdLessonExerciseArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type RemoveManyLessonExerciseArgs = Readonly<{
  ids?: string[];
}>;

type FindByIdLessonExerciseArgs = Readonly<{
  _id?: string;
}>;

type PaginationLessonExerciseArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

const transformLessonExerciseResponse =
  (type: 'paginationLessonExerciseQuery') =>
  (response: PaginationLessonExerciseResponse): PaginationLessonExerciseResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const lessonExercisesTagTypes = ['lesson-exercises'] as const;

export const getLessonExercisesQuery = (builder: BuilderType) => {
  return {
    createOneLessonExercise: builder.mutation<unknown, CreateOneLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneLessonExerciseMutation),
          variables: {
            record: args?.record,
          },
        }),
      }),
    }),

    updateOrderExerciseLesson: builder.mutation<unknown, UpdateOrderExerciseLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateOrderExerciseLessonMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    removeByIdLessonExercise: builder.mutation<unknown, RemoveByIdLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdLessonExerciseMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    updateByIdLessonExercise: builder.mutation<unknown, UpdateByIdLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdLessonExerciseMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    removeManyLessonExercise: builder.mutation<unknown, RemoveManyLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeManyLessonExerciseMutation),
          variables: {
            ids: args?.ids,
          },
        }),
      }),
    }),

    findByIdLessonExercise: builder.query<unknown, FindByIdLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdLessonExerciseQuery),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    paginationLessonExercise: builder.query<PaginationLessonExerciseResult, PaginationLessonExerciseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'lessonExercise',
              sortType: 'LessonExerciseSortEnum',
              fragment: lessonExerciseFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? 'CREATED_AT_ASC',
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['lesson-exercises'],
      transformResponse: transformLessonExerciseResponse('paginationLessonExerciseQuery'),
    }),
  };
};
